import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";

import { LktNotificationEmptyComponent } from "../notification-empty/notification-empty.component";
import { LktSwitchComponent } from "../switch/switch.component";
import {
  LktNotificationPreferenceChannel,
  LktNotificationPreferenceRow
} from "../../types/notification-item.type";

/**
 * Uma coluna de canal: o rótulo do cabeçalho, o sufixo estável do `data-cy` e a preposição que
 * distingue o nome acessível dos dois interruptores da mesma linha.
 *
 * Local e não exportado de propósito: é o vocabulário interno do template desta vista, não parte da
 * API que o consumidor liga.
 */
interface PreferenceChannelColumn {
  readonly id: LktNotificationPreferenceChannel;
  readonly label: string;
  /** Entra no `aria-label` depois do rótulo da linha: "Movimentação de equipe, na plataforma". */
  readonly ariaSuffix: string;
}

/**
 * Vista de preferências do painel de notificações (DEV-872, CA 06 e CA 07).
 *
 * Presentational puro: recebe as linhas já resolvidas pelo servidor (`label` e
 * `description` chegam traduzidos, o front não traduz nada) e emite a intenção
 * de alternar uma chave EM UM CANAL. Quem faz o PATCH — um interruptor por
 * chamada, otimista com rollback — é o container, e é ele quem devolve a lista
 * pelo input. Se o container ignorar o evento, o interruptor fica visualmente
 * ligado: a fonte da verdade do estado é a lista de entrada, não este
 * componente.
 *
 * Duas colunas de canal, "Plataforma" e "E-mail" (DEV-877): o canal de e-mail
 * entrou em produção e o contrato do backend passou a guardar um interruptor
 * por canal, independentes entre si — desligar um não desliga o outro. O
 * desenho das duas colunas é o do protótipo (`css/notificacoes.css:224-234`), e
 * é por causa da segunda coluna que a vista é mais larga que a lista.
 *
 * As duas colunas saem de UMA declaração (`channels()`): cabeçalho, `data-cy`,
 * nome acessível e ordem vêm todos dela, então não há como o rótulo de coluna
 * dessincronizar do interruptor que ele intitula.
 */
@Component({
  selector: "lkt-notification-preferences",
  standalone: true,
  imports: [LktNotificationEmptyComponent, LktSwitchComponent],
  templateUrl: "./notification-preferences.component.html",
  styleUrl: "./notification-preferences.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[attr.data-cy]": "dataCy()"
  }
})
export class LktNotificationPreferencesComponent {
  public readonly groups = input<LktNotificationPreferenceRow[]>([]);
  public readonly criticalAlerts = input<LktNotificationPreferenceRow[]>([]);

  public readonly criticalTitle = input<string>("Alertas que exigem ação");
  public readonly footerNote = input<string>("Vale para este dispositivo e todos os outros.");

  /**
   * Rótulos curtos de propósito — "Plataforma" e "E-mail", não "Na plataforma" e "Por e-mail": o
   * rótulo tem de caber numa linha sobre a chave, e a preposição já vive no `aria-label` de cada
   * interruptor (`js/notificacoes.js:660-663`).
   */
  public readonly panelColumnLabel = input<string>("Plataforma");
  public readonly emailColumnLabel = input<string>("E-mail");

  /** Copy nova, sem desenho de origem — ver o comentário no template. */
  public readonly emptyTitle = input<string>("Nada para configurar ainda");
  public readonly emptyDescription = input<string>(
    "Quando houver tipos de notificação para o seu perfil, eles aparecem aqui."
  );

  /**
   * `true` quando a carga das preferências falhou (achado 2, Important, fix round final: a mesma
   * mentira do painel de lista existia aqui — `groups`/`criticalAlerts` vazios por FALHA se
   * apresentavam como "Nada para configurar ainda", copy de ausência real). Tem precedência sobre
   * `isEmpty()`: falha e ausência real nunca se confundem.
   */
  public readonly hasError = input<boolean>(false);
  public readonly errorTitle = input<string>("Não foi possível carregar suas preferências");
  public readonly errorDescription = input<string>("Verifique sua conexão e tente novamente.");

  /**
   * Re-revisão (final fix 2, achado 1): a mentira DURÁVEL (erro parado) foi fechada por `hasError`,
   * mas a janela de CARGA continuava afirmando ausência — `isEmpty()` não tinha termo de status
   * nenhum, então "Nada para configurar ainda" renderizava durante toda requisição (primeira
   * entrada na vista, troca de identidade e, o caminho novo, a cada clique em "Tentar novamente":
   * o retry limpa a cópia de erro e mostra a de ausência falsa antes do resultado chegar). Default
   * `true` para não quebrar quem já consome este componente sem essa noção de status — só o
   * container de notificações passa `false` enquanto a carga está em voo.
   *
   * ATENÇÃO a quem for montar um segundo consumidor: **ligue este input**. Deixá-lo no default
   * reverte em silêncio ao comportamento antigo e a janela de carga volta a afirmar ausência.
   * O default existe pela convenção da lib (aqui todo input tem default), não porque seja opcional
   * em sentido funcional.
   */
  public readonly isReady = input<boolean>(true);

  public readonly dataCy = input<string>("notifications-preferences");

  /**
   * `toggle` é nome de evento nativo (`<details>` e a Popover API), então
   * `@angular-eslint/no-output-native` acusa. O nome é mantido porque é o
   * contrato acordado com o container, e o binding do output tem precedência
   * sobre o listener nativo — não há ambiguidade em tempo de execução. Mesmo
   * precedente do `select` em `notification-item.component.ts`.
   *
   * `channel` volta ao container como valor opaco, junto do `toggleKey`: os dois vieram de fora e
   * voltam byte a byte, porque quem sabe o que significam é o contrato, não esta vista.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  public readonly toggle = output<{
    readonly toggleKey: string;
    readonly channel: LktNotificationPreferenceChannel;
    readonly enabled: boolean;
  }>();

  /** Retentativa da carga que falhou (achado 2, Important). */
  public readonly retry = output<void>();

  /**
   * A ordem é a do contrato e a da tela: painel primeiro, e-mail depois (o backend ordena por
   * `Value` pela mesma razão). Cabeçalho e linhas percorrem ESTA lista, e é isso que garante que o
   * rótulo de coluna intitule o interruptor debaixo dele — em vez de os dois viverem em seis
   * lugares de markup que podem dessincronizar.
   *
   * Cobre cabeçalho, ordem, `data-cy` e nome acessível. NÃO cobre o estado: ele mora em campo
   * próprio da linha, e um canal novo pede mais que um item aqui (a lista completa está no
   * comentário da grade, em `notification-preferences.component.scss`).
   */
  public readonly channels = computed<PreferenceChannelColumn[]>(() => [
    { id: "panel", label: this.panelColumnLabel(), ariaSuffix: "na plataforma" },
    { id: "email", label: this.emailColumnLabel(), ariaSuffix: "por e-mail" }
  ]);

  /**
   * `isReady()` primeiro: sem ele, uma carga em voo (`groups`/`criticalAlerts` ainda no default
   * vazio) passaria por "vazio real" antes da resposta chegar — a mesma mentira que `hasError` já
   * fecha para o caso de falha, agora para o caso de carga.
   */
  public readonly isEmpty = computed<boolean>(
    () => this.isReady() && this.groups().length === 0 && this.criticalAlerts().length === 0
  );

  public readonly emptyDataCy = computed<string>(() => `${this.dataCy()}-empty`);
  public readonly errorDataCy = computed<string>(() => `${this.dataCy()}-error`);

  public isChannelEnabled(
    row: LktNotificationPreferenceRow,
    column: PreferenceChannelColumn
  ): boolean {
    return column.id === "panel" ? row.panelEnabled : row.emailEnabled;
  }

  /**
   * O sufixo do `data-cy` é o `toggleKey` cru — único identificador estável da linha, já que o
   * `label` é copy traduzida do servidor e o índice do `@for` é volátil — seguido do identificador
   * do canal, também cru. O canal entra porque a linha tem DOIS interruptores: sem ele o seletor
   * casaria os dois.
   */
  public channelDataCy(row: LktNotificationPreferenceRow, column: PreferenceChannelColumn): string {
    return `notification-preference-${row.toggleKey}-${column.id}`;
  }

  /**
   * Nome acessível de cada interruptor. O rótulo da linha sozinho não serve com duas colunas: o
   * leitor de tela anunciaria dois "Movimentação de equipe" indistinguíveis.
   */
  public channelAriaLabel(
    row: LktNotificationPreferenceRow,
    column: PreferenceChannelColumn
  ): string {
    return `${row.label}, ${column.ariaSuffix}`;
  }

  /**
   * Um único handler para as linhas de grupo e as de alerta crítico, nos dois canais: para o
   * backend as quatro combinações são a mesma coisa — uma chave, um canal e um booleano.
   */
  public onToggle(
    row: LktNotificationPreferenceRow,
    column: PreferenceChannelColumn,
    enabled: boolean
  ): void {
    this.toggle.emit({ toggleKey: row.toggleKey, channel: column.id, enabled });
  }

  public onRetry(): void {
    this.retry.emit();
  }
}
